/**
 * Which card pays for a purchase (LIS-1173, the purchase design's §2.8):
 * the payment policy's brands decide which of the buyer's saved cards it
 * takes (a listed brand, or a card the vault could not classify), and say
 * why it refuses one, in the seller's name. The card slot's other rules
 * (a saved card's code held only briefly, "Use another card") are today's
 * (`card-choice.ts`).
 */
import { cardBrandTaken, type PaymentPolicy } from "@libs/platform-wire/purchase";
import type { CardSummary } from "@libs/identity-onboarding-contracts/card";
import { cvcFresh, type CardChoice, type CardSlot } from "../card-choice";

/** "Visa, Mastercard or Maestro". */
const listOf = (names: readonly string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names.at(-1)}` : (names[0] ?? "");

/** Whether the purchase's checkout takes this card. */
export const cardTaken = (payment: PaymentPolicy, card: Pick<CardSummary, "brandId">): boolean =>
  cardBrandTaken(payment.brands?.map((brand) => brand.id) ?? null, card.brandId);

/** Why the seller won't take this card, naming what it does take ("Skiddle
 *  doesn’t take American Express. Use a Visa, Mastercard or Maestro
 *  card."); null where it takes it. */
export function cardRefusal(
  payment: PaymentPolicy,
  seller: { name: string },
  card: Pick<CardSummary, "brand" | "brandId">,
): string | null {
  if (cardTaken(payment, card)) return null;
  const takes = listOf(payment.brands?.map((brand) => brand.name) ?? []);
  const article = /^[aeiou]/i.test(takes) ? "an" : "a";
  return `${seller.name} doesn’t take ${card.brand.trim() || "this card"}. Use ${article} ${takes} card.`;
}

/** The card slot for a purchase: the first saved card its checkout takes
 *  is chosen, a refused one says why and is never usable. */
export function purchaseCardSlotOf(
  cards: readonly CardSummary[],
  choice: CardChoice,
  now: number,
  payment: PaymentPolicy,
  seller: { name: string },
): CardSlot {
  const taken = (card: CardSummary) => cardTaken(payment, card);
  const selected = choice.newCard
    ? null
    : ((choice.cardId === null ? cards.find(taken) : cards.find((card) => card.id === choice.cardId)) ?? null);
  const chip = selected ?? cards.find(taken) ?? cards[0] ?? null;
  const refusal = chip ? cardRefusal(payment, seller, chip) : null;
  const ready = !refusal && cvcFresh(selected, now);
  return {
    selected,
    ready,
    fields: !selected ? "card" : ready || refusal ? null : "cvc",
    fieldsKey: selected?.id ?? "new",
    chip,
    refusal,
  };
}
